// §19.4 — the shell UI portal: dialogs, pickers and permission prompts, rendered **above every
// `.win-host`** with a shell-owned focus trap.
//
// The trap is here, not in the skin. A skin decides what a dialog looks like; whether an app
// can tab out of one is not negotiable, because "a user gesture is the grant" (§9.5) is only
// meaningful if the gesture cannot have been aimed at something else. The picker gets the same
// trap for exactly the same reason.
import { useEffect, useRef, type RefObject } from 'react';
import { useDialog, useKernel, useStoreValue } from '@/kernel/react';
import { useSkin } from './SkinProvider';
import { FilePicker } from './FilePicker';
import { SharePicker } from './SharePicker';
import { ConnectionPicker } from './ConnectionPicker';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Hold focus inside `container` while `active`, restore it afterwards, and route Escape.
 * `onEscape` being null means this surface cannot be dismissed that way (an alert).
 */
function useFocusTrap(
  active: boolean,
  container: RefObject<HTMLElement | null>,
  onEscape: (() => void) | null,
): void {
  useEffect(() => {
    if (!active) return;
    const previous = document.activeElement as HTMLElement | null;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && onEscape) {
        event.preventDefault();
        event.stopPropagation();
        onEscape();
        return;
      }
      if (event.key !== 'Tab') return;

      const node = container.current;
      if (!node) return;
      const focusable = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      const activeElement = document.activeElement;

      // Wrap in both directions, and pull focus back in if it somehow escaped.
      if (!node.contains(activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // Capture phase: an app's own key handling must never see these first.
    document.addEventListener('keydown', onKeyDown, true);

    const onFocusIn = (event: FocusEvent): void => {
      const node = container.current;
      if (!node || node.contains(event.target as Node)) return;
      node.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    };
    document.addEventListener('focusin', onFocusIn, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn, true);
      previous?.focus?.();
    };
  }, [active, container, onEscape]);
}

export function ShellUiHost() {
  const kernel = useKernel();
  const skin = useSkin();
  const entry = useDialog();
  const picker = useStoreValue(kernel.shellui.store, (state) => state.pickers[0]);
  const share = useStoreValue(kernel.shellui.store, (state) => state.shares[0]);
  // §34.13 — the roster picker joins the same portal, the same trap and the same one-at-a-time rule.
  const roster = useStoreValue(kernel.shellui.store, (state) => state.rosters[0]);
  const trapRef = useRef<HTMLDivElement | null>(null);
  const Dialog = skin.components.Dialog;

  // A picker, a share, a roster and a dialog can all be pending; the file picker is on top, then the
  // share, then the roster, and whichever is showing owns the trap. Escape always cancels the one
  // you are looking at.
  const escape = picker
    ? () => kernel.shellui.cancelPicker(picker.request.id)
    : share
      ? () => kernel.shellui.cancelShare(share.request.id)
      : roster
        ? () => kernel.shellui.cancelRoster(roster.request.id)
        : entry && entry.request.kind !== 'alert'
          ? () => kernel.shellui.cancel(entry.request.id)
          : null;

  useFocusTrap(Boolean(picker ?? share ?? roster ?? entry), trapRef, escape);

  if (!picker && !share && !roster && !entry) return null;

  return (
    <div className="shellui-scrim" data-testid="shellui-scrim" ref={trapRef}>
      {picker ? (
        <FilePicker />
      ) : share ? (
        <SharePicker />
      ) : roster ? (
        <ConnectionPicker />
      ) : entry ? (
        <Dialog
          request={entry.request}
          appId={entry.appId}
          onAccept={(value) => kernel.shellui.accept(entry.request.id, value)}
          onCancel={() => kernel.shellui.cancel(entry.request.id)}
        />
      ) : null}
    </div>
  );
}
